import type * as React from 'react';

import type { SkillMasteryLevelFragment } from 'bundles/levelsets/levelsets-v2/api/__generated__/SkillMasteryLevelsForCurrentUser';
import { MasteryLevel1SVG } from 'bundles/levelsets/levelsets-v2/assets/MasteryLevel1SVG';
import { MasteryLevel2SVG } from 'bundles/levelsets/levelsets-v2/assets/MasteryLevel2SVG';
import { MasteryLevel3SVG } from 'bundles/levelsets/levelsets-v2/assets/MasteryLevel3SVG';
import { MasteryLevel4SVG } from 'bundles/levelsets/levelsets-v2/assets/MasteryLevel4SVG';

import _t from 'i18n!nls/levelsets';

export type MasteryLevel = SkillMasteryLevelFragment['masteryLevel'];

export type LevelSetScore = {
  masteryLevel: MasteryLevel;
  masteryLevelScore: number;
};

export type LevelFeedbackDetails = {
  header: string;
  checklist: Array<string>;
};

export type LevelFeedback = {
  name: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  details: LevelFeedbackDetails;
};

export type MasteryLevelLabel = {
  name: string;
  shortName: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

export const getSkillsMasteryLevelLabels = (): Record<MasteryLevel, MasteryLevelLabel> => ({
  MASTERY_LEVEL_1: {
    name: _t('Mastery Level 1'),
    shortName: _t('Level 1'),
    description: _t('Understand'),
    icon: MasteryLevel1SVG,
  },
  MASTERY_LEVEL_2: {
    name: _t('Mastery Level 2'),
    shortName: _t('Level 2'),
    description: _t('Apply'),
    icon: MasteryLevel2SVG,
  },
  MASTERY_LEVEL_3: {
    name: _t('Mastery Level 3'),
    shortName: _t('Level 3'),
    description: _t('Analyze and Evaluate'),
    icon: MasteryLevel3SVG,
  },
  MASTERY_LEVEL_4: {
    name: _t('Mastery Level 4'),
    shortName: _t('Level 4'),
    description: _t('Create and Innovate'),
    icon: MasteryLevel4SVG,
  },
});

export const getCoverPageLabels = () => {
  const masteryLevelLabels = getSkillsMasteryLevelLabels();
  return {
    header: {
      heading: _t('Assess your skill mastery in 15 minutes'),
      checklist: [
        {
          textContent: _t('View your current skill mastery progress,'),
          linkText: _t('learn about Coursera’s skill mastery framework.'),
        },
        { textContent: _t('Get personalized course recommendations based on your assessment.') },
        { textContent: _t('Complete the assessment in one session to avoid starting over.') },
      ],
    },
    bodyHeading: _t('Mastery levels determined by this assessment'),
    levels: [
      {
        name: masteryLevelLabels.MASTERY_LEVEL_1.name,
        description: masteryLevelLabels.MASTERY_LEVEL_1.description,
        icon: masteryLevelLabels.MASTERY_LEVEL_1.icon,
      },
      {
        name: masteryLevelLabels.MASTERY_LEVEL_2.name,
        description: masteryLevelLabels.MASTERY_LEVEL_2.description,
        icon: masteryLevelLabels.MASTERY_LEVEL_2.icon,
      },
      {
        name: masteryLevelLabels.MASTERY_LEVEL_3.name,
        description: masteryLevelLabels.MASTERY_LEVEL_3.description,
        icon: masteryLevelLabels.MASTERY_LEVEL_3.icon,
      },
    ],
  };
};

export const getFeedbackPageLabels = (
  currentLevel: MasteryLevel | null,
  isFirstLevelInProgress: boolean,
  skillName: string
) => {
  const masteryLevelLabels = getSkillsMasteryLevelLabels();
  let header;

  switch (currentLevel) {
    case null:
      header = isFirstLevelInProgress
        ? {
            title: _t('You’re making great progress to Mastery Level 1!'),
          }
        : {
            title: _t('Get started with a recommended course'),
          };
      break;
    case 'MASTERY_LEVEL_1':
      header = {
        title: _t('You’re making great progress to Mastery Level 2!'),
      };
      break;
    case 'MASTERY_LEVEL_2':
      header = {
        title: _t('You’re making great progress to Mastery Level 3!'),
      };
      break;
    case 'MASTERY_LEVEL_3':
      header = {
        title: _t('You’ve achieved Mastery Level 3!'),
      };
      break;
    default:
      header = {};
  }

  const levels: Array<LevelFeedback> = [
    {
      name: masteryLevelLabels.MASTERY_LEVEL_1.name,
      description: masteryLevelLabels.MASTERY_LEVEL_1.description,
      icon: masteryLevelLabels.MASTERY_LEVEL_1.icon,
      details: {
        header: _t('To attain Mastery Level 1, you’ll need to:'),
        // TODO: Update when copy is avaialble from the designs
        checklist: [
          _t('Recall and recognize key concepts and principles.'),
          _t('Describe and summarize foundational elements.'),
          _t('Interpret and exemplify basic ideas and knowledge.'),
        ],
      },
    },
    {
      name: masteryLevelLabels.MASTERY_LEVEL_2.name,
      description: masteryLevelLabels.MASTERY_LEVEL_2.description,
      icon: masteryLevelLabels.MASTERY_LEVEL_2.icon,
      details: {
        header: _t('To attain Mastery Level 2, you’ll need to:'),
        checklist: [
          _t('Use and implement knowledge (e.g. directly apply a formula or rule).'),
          _t('Execute tasks accurately with some adaptability.'),
          _t('Solve problems by adjusting methods to different scenarios.'),
        ],
      },
    },
    {
      name: masteryLevelLabels.MASTERY_LEVEL_3.name,
      description: masteryLevelLabels.MASTERY_LEVEL_3.description,
      icon: masteryLevelLabels.MASTERY_LEVEL_3.icon,
      details: {
        header: _t('To attain Mastery Level 3, you’ll need to:'),
        checklist: [
          _t('Differentiate and distinguish between concepts.'),
          _t('Attribute causes or underlying principles.'),
          _t('Evaluate, justify decisions based on criteria.'),
        ],
      },
    },
  ];

  return {
    header,
    body: {
      levels,
    },
    footer: {
      heading: _t('Your next steps in #{skillName}', { skillName }),
      description: _t('Tailored courses to fill your knowledge gaps and advance skill mastery.'),
    },
  };
};

export const getRecommendationsPageChecklist = () => [
  { textContent: _t('Progress faster with courses matched to your assessment results.') },
  { textContent: _t('Engage with any of these expert-led courses to further develop your skill mastery.') },
  {
    textContent: _t('Complete a course and reassess your mastery level to track your progress. '),
    linkText: _t('Learn more about mastery levels.'),
  },
];

export const AUTHENTICATION_MODAL_MODES = {
  COMPLETE: 'complete',
  ERROR: 'error',
  EXPIREDLINK: 'expired',
  FORGOT: 'forgot',
  LINKED: 'linked',
  LOGIN: 'login',
  MAGICLINK: 'magiclink',
  ORGANIZATIONLOGIN: 'organizationlogin',
  ORGANIZATIONSIGNUP: 'organizationsignup',
  REQUESTMAGICLINK: 'requestmagiclink',
  SIGNUP: 'signup',
  SSO: 'sso',
} as const;
